import React, { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Phone, MessageSquare, Mail, Play, Pause, RotateCcw, Volume2, VolumeX } from 'lucide-react';

const ConversationDemo = ({ type = 'voice' }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const timerRef = useRef(null);

  const voiceConversation = [
    { speaker: 'Jason (AI)', text: 'Hi Sarah, this is Jason from Exit Realty Specialists. You showed interest in a property listing in the Saint John area. Is this a good time to chat?', delay: 2000, role: 'ai' },
    { speaker: 'Sarah', text: 'Yes, hi Jason. I did see that listing. I\'m looking for a 3-bedroom home in the area.', delay: 2500, role: 'client' },
    { speaker: 'Jason (AI)', text: 'Perfect! I work with clients looking to invest or buy in the Greater Saint John area. Have you been pre-approved for a mortgage, or are you still exploring your options?', delay: 3500, role: 'ai' },
    { speaker: 'Sarah', text: 'I\'m pre-approved for up to $400,000. I\'m ready to move forward quickly.', delay: 2200, role: 'client' },
    { speaker: 'Jason (AI)', text: 'That\'s excellent news! With that budget, you have some great options in Saint John. Are you looking in a specific neighborhood?', delay: 3000, role: 'ai' },
    { speaker: 'Sarah', text: 'I prefer the Uptown or North End areas. Close to schools would be ideal.', delay: 2300, role: 'client' },
    { speaker: 'Jason (AI)', text: 'Perfect! Let me connect you with Justin Saulnier, our licensed REALTOR® who specializes in those areas. He can show you properties that fit your criteria. Would you be available for a call tomorrow at 2 PM?', delay: 4000, role: 'ai' },
    { speaker: 'Sarah', text: 'Yes, that works for me!', delay: 1500, role: 'client' },
    { speaker: 'Jason (AI)', text: 'Wonderful! I\'m sending a calendar invite to your email right now. Justin\'s direct line is +1-506-647-4510 if you need to reach him. Looking forward to helping you find your perfect home!', delay: 3500, role: 'ai' },
    { speaker: 'System', text: '✓ Lead qualified as HOT\n✓ Synced to Follow Up Boss\n✓ Calendar invite sent\n✓ Agent notified', delay: 2000, isSystem: true }
  ];

  const smsConversation = [
    { speaker: 'Jason (AI)', text: 'Hi Michael! This is Jason from Exit Realty. You inquired about properties in Saint John, NB. Are you currently looking to buy or sell?', delay: 1500, role: 'ai' },
    { speaker: 'Michael', text: 'Hi! I\'m looking to buy. What\'s available in my budget?', delay: 2000, role: 'client' },
    { speaker: 'Jason (AI)', text: 'Great! What\'s your budget range? This helps me show you the best matches.', delay: 2000, role: 'ai' },
    { speaker: 'Michael', text: 'Around $350k. Looking for 2-3 bedrooms.', delay: 1800, role: 'client' },
    { speaker: 'Jason (AI)', text: 'Perfect! We have several options in that range. When are you hoping to move?', delay: 2200, role: 'ai' },
    { speaker: 'Michael', text: 'Within 3 months ideally.', delay: 1500, role: 'client' },
    { speaker: 'Jason (AI)', text: 'Excellent timeline! I\'d love to connect you with Justin, our REALTOR® who can show you properties this week. Can I have him call you at this number?', delay: 3000, role: 'ai' },
    { speaker: 'Michael', text: 'Yes please! Afternoons work best.', delay: 1800, role: 'client' },
    { speaker: 'Jason (AI)', text: 'Perfect! Justin will call you tomorrow afternoon. His mobile is +1-506-647-4510. Check your email for property listings I\'m sending now! 🏡', delay: 3500, role: 'ai' },
    { speaker: 'System', text: '✓ Lead qualified as WARM\n✓ Added to Follow Up Boss\n✓ Property matches sent\n✓ Follow-up scheduled', delay: 2000, isSystem: true }
  ];

  const emailConversation = [
    { speaker: 'System', text: 'Email received from: jennifer.clark@email.com\nSubject: Interested in selling my property', delay: 1000, isSystem: true },
    { speaker: 'Jason (AI)', text: 'Hi Jennifer,\n\nThank you for reaching out! I\'m Jason with Exit Realty Specialists in Saint John, NB.\n\nI\'d love to help you with selling your property. To provide you with the most accurate market analysis, I have a few quick questions:\n\n• What type of property are you looking to sell?\n• When are you hoping to list?\n• Have you worked with a realtor before?\n\nI can also connect you with Justin Saulnier, our top-producing REALTOR®, for a free home valuation.\n\nBest regards,\nJason\nExit Realty Specialists', delay: 3000, role: 'ai' },
    { speaker: 'Jennifer', text: 'Hi Jason,\n\nIt\'s a 4-bedroom house in the North End. Looking to list within 2 months. I haven\'t worked with a realtor recently.\n\nJennifer', delay: 2500, role: 'client' },
    { speaker: 'Jason (AI)', text: 'Hi Jennifer,\n\nExcellent! The North End market is very strong right now. I\'d love to schedule a free consultation with Justin to:\n\n✓ Provide a comprehensive market analysis\n✓ Discuss pricing strategy\n✓ Create a customized marketing plan\n✓ Answer all your questions\n\nAre you available for a 30-minute call this week?\n\nJustin\'s contact:\n📧 justin@exitsj.ca\n📱 +1-506-647-4510\n🌐 movewithjustin.ca\n\nLooking forward to helping you!\n\nBest,\nJason', delay: 4000, role: 'ai' },
    { speaker: 'System', text: '✓ Lead qualified as HOT\n✓ Seller profile created in FUB\n✓ Market analysis prepared\n✓ Meeting scheduled', delay: 2000, isSystem: true }
  ];

  const conversations = {
    voice: voiceConversation,
    sms: smsConversation,
    email: emailConversation
  };

  const currentConversation = conversations[type];

  // Auto-scroll to bottom when new message appears
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentStep]);

  // Main animation loop with better cleanup
  useEffect(() => {
    // Clear any existing timers when playing state changes
    if (!isPlaying) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setShowTyping(false);
      return;
    }

    // Check if we've reached the end
    if (currentStep >= currentConversation.length) {
      setIsPlaying(false);
      setShowTyping(false);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Show typing indicator
    setShowTyping(true);
    
    // Set timer for next message
    timerRef.current = setTimeout(() => {
      // Check if still playing before advancing
      setShowTyping(false);
      setCurrentStep(prev => prev + 1);
    }, currentConversation[currentStep].delay);

    // Cleanup function - critical for preventing race conditions
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPlaying, currentStep, currentConversation]);

  // Component unmount cleanup
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const handlePlayPause = () => {
    // Clear any existing timers first
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    
    if (currentStep >= currentConversation.length) {
      // Restart from beginning
      setShowTyping(false);
      setCurrentStep(0);
      setIsPlaying(true);
    } else {
      setIsPlaying(!isPlaying);
    }
  };

  const handleReset = () => {
    // Clear all timers immediately
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    // Reset all state
    setIsPlaying(false);
    setShowTyping(false);
    setCurrentStep(0);
  };

  const getIcon = () => {
    switch (type) {
      case 'voice': return Phone;
      case 'sms': return MessageSquare;
      case 'email': return Mail;
      default: return Phone;
    }
  };

  const Icon = getIcon();
  const displayedMessages = currentConversation.slice(0, currentStep);

  return (
    <Card className="bg-slate-900 border-slate-700 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">
              {type === 'voice' && 'Voice Call Demo'}
              {type === 'sms' && 'SMS Demo'}
              {type === 'email' && 'Email Demo'}
            </h3>
            <p className="text-sm text-gray-400">See Jason AI in action</p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          <Button
            onClick={handlePlayPause}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isPlaying ? (
              <>
                <Pause className="w-4 h-4 mr-2" />
                Pause
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                {currentStep >= currentConversation.length ? 'Replay' : 'Play'}
              </>
            )}
          </Button>
          <Button
            onClick={handleReset}
            size="sm"
            variant="outline"
            className="border-slate-600 text-white hover:bg-slate-800"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="w-full bg-slate-700 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / currentConversation.length) * 100}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-400">
          <span>Step {currentStep} of {currentConversation.length}</span>
          <span>{Math.round((currentStep / currentConversation.length) * 100)}%</span>
        </div>
      </div>

      {/* Messages */}
      <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
        {displayedMessages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.isSystem ? 'justify-center' : message.role === 'client' ? 'justify-end' : 'justify-start'} animate-fade-in`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-4 ${
                message.isSystem
                  ? 'bg-green-500/10 border border-green-500/30 text-green-300'
                  : message.role === 'client'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-800 text-gray-100 border border-slate-700'
              }`}
            >
              <div className="text-xs font-semibold mb-1 opacity-80">{message.speaker}</div>
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{message.text}</div>
            </div>
          </div>
        ))}

        {/* Typing Indicator */}
        {showTyping && isPlaying && (
          <div className="flex justify-start animate-fade-in">
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-xs text-gray-400">Jason is typing...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Footer Stats */}
      {currentStep >= currentConversation.length && (
        <div className="mt-6 pt-6 border-t border-slate-700">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-400">~{Math.round(currentConversation.reduce((acc, msg) => acc + msg.delay, 0) / 1000)}s</div>
              <div className="text-xs text-gray-400">Total Time</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-400">{currentConversation.length}</div>
              <div className="text-xs text-gray-400">Messages</div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </Card>
  );
};

export default ConversationDemo;
